<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Input, Select, Text } from '@Shelly/shelly-design-system';
	import {
		SERIAL_MODES_DESCRIPTIONS,
		getSupportedModeOptions,
	} from 'src/helpers/addons/addon_RS485_serial_helpers';
	import {
		SERIAL_FORMAT_VALUES,
		SERIAL_BAUD_RATES,
		SERIAL_MODE_ICONS,
		SERIAL_MODES_MAP,
	} from 'src/helpers/addons/addon_serial_helpers';
	//Types
	import type { SerialConfig } from 'src/types/SerialConfig';
	import type { Peripheral } from 'src/types/Peripherals';

	export let peripheral: Peripheral;
	export let settings: SerialConfig;
	export let error = false;

	const baudOptions = SERIAL_BAUD_RATES.map((value) => ({
		value: String(value),
		title: String(value),
	}));

	const formatOptions = SERIAL_FORMAT_VALUES.map((value) => ({
		value,
		title: value.toUpperCase(),
	}));

	const option = (options: { value: string; title: string }[], value: string, uppercase = false) =>
		options.find((item) => item.value?.toUpperCase() === value?.toUpperCase()) ?? {
			value,
			title: uppercase ? String(value?.toUpperCase()) : String(value),
		};

	const changeMode = (mode: string) => {
		settings = { ...settings, mode };
	};

	let currentMode: SerialConfig['mode'];
	let currentBaud: number;
	let currentFormat: string;
	let serverAddress: number;
	let modeDescription: (typeof SERIAL_MODES_DESCRIPTIONS)[string];
	let modeOptions: { value: string; title: string }[] = [];

	$: {
		currentMode = settings?.mode ?? SERIAL_MODES_MAP.disabled;
		currentBaud = settings?.serial?.baud ?? 115200;
		currentFormat = settings?.serial?.format ?? '8N1';
		serverAddress = settings?.mb_server?.addr ?? 1;
		modeDescription = SERIAL_MODES_DESCRIPTIONS[currentMode];
		modeOptions = getSupportedModeOptions(peripheral);
		error =
			currentMode === SERIAL_MODES_MAP.mbServer &&
			(!Number.isInteger(serverAddress) || serverAddress < 1 || serverAddress > 247);
	}
</script>

<Text styleType="h2" class="mb-s">
	{$format('label_serial_mode', { default: 'Operating mode' })}
</Text>
<Select
	class="mb-s"
	size="m"
	icon={SERIAL_MODE_ICONS[currentMode] ?? 'fal fa-wave-square'}
	value={option(modeOptions, currentMode)}
	options={modeOptions}
	id="select-rs485-mode"
	aria-label={$format('label_serial_mode', { default: 'Operating mode' })}
	on:change={(event) => changeMode(String(event.detail['value']))}
/>
<Text styleType="body-small" class="mb-m font-italic">
	{modeDescription ? $format(modeDescription.key, { default: modeDescription.default }) : currentMode}
</Text>

{#if currentMode !== SERIAL_MODES_MAP.disabled}
	<Text styleType="h2" class="mb-s">{$format('label_serial_baud', { default: 'Baud rate' })}</Text>
	<Select
		class="mb-s"
		size="m"
		icon="fal fa-gauge-high"
		value={option(baudOptions, String(currentBaud))}
		options={baudOptions}
		id="select-rs485-baud"
		aria-label={$format('label_serial_baud', { default: 'Baud rate' })}
		on:change={(event) =>
			(settings = {
				...settings,
				serial: { ...settings.serial, baud: Number(event.detail['value']) },
			})}
	/>
	<Text styleType="body-small" class="mb-m font-italic">
		{$format('description_serial_baud', {
			default: 'Number of symbol changes (signaling events) per second.',
		})}
	</Text>

	<Text styleType="h2" class="mb-s">{$format('label_serial_format', { default: 'Format' })}</Text>
	<Select
		class="mb-s"
		size="m"
		icon="fal fa-code"
		value={option(formatOptions, currentFormat, true)}
		options={formatOptions}
		id="select-rs485-format"
		aria-label={$format('label_serial_format', { default: 'Format' })}
		on:change={(event) =>
			(settings = {
				...settings,
				serial: { ...settings.serial, format: String(event.detail['value']) },
			})}
	/>
	<Text styleType="body-small" class="mb-m font-italic">
		{$format('description_serial_format', {
			default: 'A 3-character string indicating the serial frame format to use, e.g. 8N1.',
		})}
	</Text>
{/if}
{#if currentMode === SERIAL_MODES_MAP.mbServer}
	<Text styleType="h2" class="mb-s"
		>{$format('label_modbus_server_address', { default: 'Modbus server address' })}</Text
	>
	<Input
		type="number"
		size="m"
		value={String(serverAddress)}
		error={error
			? $format('min_max_error_description', {
					values: { min: 1, max: 247 },
					default: 'Value must be between 1 and 247.',
				})
			: ''}
		id="input-rs485-server-address"
		aria-label={$format('label_modbus_server_address', { default: 'Modbus server address' })}
		on:input={(event) => {
			const addr = Number(event.currentTarget['value'] ?? 1);
			settings = { ...settings, mb_server: { ...settings.mb_server, addr } };
		}}
	/>
{/if}
